import { useContext, useEffect, useState } from "react";
import { Container } from "@material-ui/core";
import { Button } from "../../../components/ui/button";
import PageTitle from "../../../components/pageTitle/PageTitle";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Checkbox } from "../../../components/ui/checkbox";
import { Label } from "../../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../../components/ui/alerts";

import {
  Shield,
  Trash2,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  FileText,
  Database,
  Lock,
} from "lucide-react";
import { serverTimestamp } from "firebase/firestore";
import { updateSettings } from "../../../services/apiservices/settings";
import AuthContext from "../../../hooks/authContext";
import { useToast } from "../../../hooks/useToast";
import { useNavigate } from "react-router";

const PrivacyDataSettings = () => {
  const [deleteConfirmChecked, setDeleteConfirmChecked] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionScheduled, setDeletionScheduled] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [daysRemaining] = useState(14);
  const auth = useContext(AuthContext);
  const { toast } = useToast();
  const navigate = useNavigate();


  useEffect(() => {
    if(!auth?.userProfile) return;

    setDeleteConfirmChecked(auth?.userProfile?.delete ? true : false);
    setDeletionScheduled(auth?.userProfile?.delete ? true : false);
  }, [auth])

  const handleDeleteRequest = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDeletion = async () => {
    try {
      const deletedBy = {
        id: auth?.userProfile?.id,
        name: `${auth?.userProfile?.firstName} ${auth?.userProfile?.lastName}`,
        email: auth?.userProfile?.email,
      };

      const payload = {
        delete: true,
        deletedBy,
        deletedAt: serverTimestamp(),
      };

      await updateSettings(payload, "users", auth?.userProfile?.id);

      setDeletionScheduled(true);
      setShowDeleteModal(false);
      setDeleteConfirmChecked(false);

      toast?.({
        title: "Deletion Scheduled",
        description: "Your company has been scheduled for permanent deletion.",
      });
      navigate("/");
    } catch (error) {
      console.error("Company deletion failed", error);
      toast?.({
        title: "Error",
        description: "Failed to schedule company deletion.",
        variant: "destructive",
      });
    }
  };

  const handleCancelDeletion = () => {
    setDeletionScheduled(false);
  };

  return (
    <>
      <Container>
        <PageTitle
          pageTitle="Privacy & Data Settings"
          showBreadcrumb={[
            { label: "Home", to: "/" },
            { label: "Settings", to: "/account/settings" },
          ]}
          BreadCrumbCurrentPageTitle={"Privacy & Data Settings"}
          showLastSeen={true}
        />

        <div className="max-w-4xl mx-auto w-full space-y-6 md:space-y-8">
          {/* Deletion Scheduled Banner */}
          {deletionScheduled && (
            <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-900/20 mt-20">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
              <AlertTitle className="text-amber-800 dark:text-amber-400">
                Company scheduled for deletion
              </AlertTitle>
              <AlertDescription className="text-amber-700 dark:text-amber-300">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-8">
                  <span>
                    {daysRemaining} days remaining before permanent deletion
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelDeletion}
                    className="border-amber-600 text-amber-700 hover:bg-amber-100 dark:border-amber-500 dark:text-amber-400 dark:hover:bg-amber-900/30"
                  >
                    Cancel Deletion
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          <div className="sectionBox dark:bg-[#1A1F2C] px-24 py-16">
            {/* Page Header */}
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-8">
                Privacy & Data
              </h2>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                How ProTeams manages your company's data
              </p>
            </div>

            {/* Section 1: Privacy & Data Overview */}
            <div className="space-y-4 md:space-y-6 gap-3 mt-20">
              {/* Card 1: Data Handling Overview */}
              <Card className={"w-full bg-white dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]"}>
                <CardHeader className="pb-12 pt-24 ph-24">
                  <div className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base md:text-lg">
                      Data Handling Overview
                    </CardTitle>
                  </div>
                  <CardDescription className="text-sm">
                    Understanding what happens to your data
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 md:space-y-6 pt-0 pb-24 ph-24">
                  {/* Data That Is Deleted */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 dark:text-white text-sm md:text-base flex items-center gap-2">
                      <Trash2 className="h-4 w-4 text-gray-500" />
                      Data That Is Deleted When a Company Is Permanently Removed
                    </h4>

                    <div className="ml-24 space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <div>• Company profile and configuration data</div>
                      <div>• Employee and workforce records</div>
                      <div>• Schedules, tasks, internal messages, and uploaded content</div>
                      <div>• Operational data created within the platform</div>
                    </div>
                  </div>

                  {/* Data That May Be Retained */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 dark:text-white text-sm md:text-base flex items-center gap-2">
                      <Lock className="h-4 w-4 text-gray-500" />
                      Data That May Be Retained
                    </h4>

                    <div className="ml-24 space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <div>• Billing and invoicing records</div>
                      <div>• Payment transaction references</div>
                      <div>• Message delivery and system logs</div>
                      <div>• Security, audit, and compliance records</div>
                    </div>
                  </div>

                  {/* Why Some Data Is Retained */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 dark:text-white text-sm md:text-base flex items-center gap-2">
                      <Shield className="h-4 w-4 text-gray-500" />
                      Why Some Data Is Retained
                    </h4>

                    <div className="ml-24 space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <div>• Legal and accounting obligations</div>
                      <div>• Fraud prevention and dispute resolution</div>
                      <div>
                        • Compliance with third-party service providers (e.g., payments and messaging services)
                      </div>
                      <div>• Backup and system integrity requirements</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Card 2: Company Deletion Lifecycle */}
              <Card className={"w-full bg-white dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]"}>
                <CardHeader className="pb-12 pt-24 ph-24">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base md:text-lg">
                      Company Deletion Lifecycle
                    </CardTitle>
                  </div>
                  <CardDescription className="text-sm">
                    The step-by-step process for company deletion
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 md:space-y-6 pt-0 pb-24 ph-24">
                  {/* Timeline Steps */}
                  <div className="space-y-4">
                    {/* Step 1: Deletion Requested */}
                    <div className="flex gap-3 md:gap-4">
                      <div className="flex flex-col items-center">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm font-medium">
                          1
                        </div>
                        <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-700 mt-8" />
                      </div>
                      <div className="flex-1 pb-16">
                        <h5 className="font-medium text-gray-900 dark:text-white text-sm md:text-base">
                          Deletion Requested
                        </h5>
                        <ul className="mt-8 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          <li>• Company owner requests deletion</li>
                          <li>• Account enters a Pending Deletion state</li>
                        </ul>
                      </div>
                    </div>

                    {/* Step 2: Cooldown Period */}
                    <div className="flex gap-3 md:gap-4">
                      <div className="flex flex-col items-center">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-sm font-medium">
                          2
                        </div>
                        <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-700 mt-8" />
                      </div>
                      <div className="flex-1 pb-16">
                        <h5 className="font-medium text-gray-900 dark:text-white text-sm md:text-base">
                          Cooldown Period
                        </h5>
                        <ul className="mt-8 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          <li>• 14-day review period</li>
                          <li>• Full access remains during this time</li>
                          <li>• Deletion can be canceled at any point</li>
                        </ul>
                      </div>
                    </div>

                    {/* Step 3: Permanent Deletion */}
                    <div className="flex gap-3 md:gap-4">
                      <div className="flex flex-col items-center">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm font-medium">
                          3
                        </div>
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900 dark:text-white text-sm md:text-base">
                          Permanent Deletion
                        </h5>
                        <ul className="mt-8 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          <li>
                            • After the cooldown, eligible company data is
                            permanently removed
                          </li>
                          <li>
                            • Certain records may continue to be retained as
                            described above
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Explanation */}
                  <div className="bg-muted/50 rounded-lg p-12 md:p-16 space-y-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Why this process exists:
                    </p>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <li>• Prevents accidental deletion</li>
                      <li>• Protects against misuse or unauthorized actions</li>
                      <li>
                        • Supports legal, financial, and compliance requirements
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Card 3: Privacy Policy */}
              <Card className={"w-full bg-white dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]"}>
                <CardHeader className="pb-12 pt-24 ph-24">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base md:text-lg">
                      Privacy Policy
                    </CardTitle>
                  </div>
                  <CardDescription className="text-sm">
                    Review our complete privacy policy for detailed information
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0 pb-24 ph-24">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => window.open("https://proteams.io/privacy-policy/", "_blank")}
                  >
                    <ExternalLink className="h-4 w-4 mr-8" />
                    View Full Privacy Policy
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Section 2: Delete Company (Danger Zone) */}
            <div className="space-y-4 pt-16 md:pt-24">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive dark:text-red-600" />
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                  Danger Zone
                </h3>
              </div>

              <Card className={"border-destructive/30 w-full bg-white dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]"}>
                <CardHeader className="pb-12 pt-24 ph-24">
                  <div className="flex items-center gap-2">
                    <Trash2 className="h-5 w-5 text-destructive dark:text-red-600" />
                    <CardTitle className="text-base md:text-lg text-destructive dark:text-red-600">
                      Delete Company
                    </CardTitle>
                  </div>
                  <CardDescription className="text-sm">
                    Permanently delete your company and all associated data
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-0 pb-24 ph-24">
                  <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-start gap-2">
                      <Clock className="h-4 w-4 mt-2 shrink-0 text-gray-500" />
                      <p>
                        Deletion is scheduled, not immediate. A 14-day review
                        period will begin after confirmation.
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Lock className="h-4 w-4 mt-2 shrink-0 text-gray-500" />
                      <p>
                        Some data cannot be deleted due to legal and compliance
                        requirements.
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Shield className="h-4 w-4 mt-2 shrink-0 text-gray-500" />
                      <p>Only company owners can request company deletion.</p>
                    </div>
                  </div>

                  <div className="pt-8 space-y-4">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="deleteConfirm"
                        checked={deleteConfirmChecked}
                        onCheckedChange={(checked) =>
                          setDeleteConfirmChecked(checked === true)
                        }
                        disabled={deletionScheduled}
                      />
                      <Label
                        htmlFor="deleteConfirm"
                        className="text-sm cursor-pointer leading-tight text-gray-700 dark:text-gray-300"
                      >
                        I understand this will schedule my company for deletion
                        after a 14-day review period.
                      </Label>
                    </div>

                    <Button
                      variant="destructive"
                      onClick={handleDeleteRequest}
                      disabled={!deleteConfirmChecked || deletionScheduled}
                      className="w-full sm:w-auto"
                    >
                      <Trash2 className="h-4 w-4 mr-8" />
                      Delete Company
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </Container>
      {/* Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive dark:text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Company Deletion
            </DialogTitle>
            <DialogDescription className="pt-8 space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                You are about to schedule your company for permanent deletion.
              </p>

              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-2 text-green-600 shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      Data that will be deleted:
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Company profile, employee records, schedules, tasks,
                      messages, and operational data.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <XCircle className="h-4 w-4 mt-2 text-amber-600 shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      Data that may be retained:
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Billing records, payment references, system logs, and
                      compliance records.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 space-y-2">
                <label className="text-sm font-medium text-gray-900 dark:text-white">
                  Type{" "}
                  <span className="font-semibold text-destructive">DELETE</span>{" "}
                  to confirm
                </label>

                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="DELETE"
                  className={`w-full h-10 px-3 rounded-md border
                    focus:outline-none focus:ring-2 focus:ring-destructive
                    dark:bg-[#020817] dark:text-white
                    ${
                      deleteConfirmation &&
                      deleteConfirmation.trim().toLowerCase() !== "delete"
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-700"
                    }`}
                />

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  This action is irreversible.
                </p>
            </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeletion}
              disabled={deleteConfirmation.trim().toLowerCase() !== "delete"}
              className="w-full sm:w-auto"
            >
              <Trash2 className="h-4 w-4 mr-8" />
              Confirm Deletion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PrivacyDataSettings;
